// React hook + component for resolving feature flags.
//
// Both wrap the pure `evaluateFlag` in `featureFlags.ts` against the
// current auth state from Zustand. Re-renders only when the relevant
// auth slice changes — `roles` and `email` are arrays/strings, so
// Zustand's default referential equality already keeps this tight.

import type { ReactNode } from 'react';
import { useAuthStore } from '@/store/authStore';
import { evaluateFlag, type FeatureFlagKey } from '@/featureFlags';

export function useFeatureFlag(key: FeatureFlagKey): boolean {
  const user      = useAuthStore((s) => s.user);
  const overrides = useAuthStore((s) => s.flagOverrides);
  return evaluateFlag(key, {
    email:         user?.email ?? null,
    roles:         user?.roles ?? [],
    isAuthed:      user != null,
    flagOverrides: overrides,
  });
}

interface Props {
  flag:     FeatureFlagKey;
  /** Optional fallback rendered when the flag is OFF. Default: nothing. */
  fallback?: ReactNode;
  children: ReactNode;
}

/** Inline gate. Same resolution as the hook, just sugar for JSX. */
export function FeatureFlag({ flag, fallback = null, children }: Props) {
  const on = useFeatureFlag(flag);
  return on ? <>{children}</> : <>{fallback}</>;
}
